import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import CustomerSelect from '../components/CustomerSelect';
import { selectAccountData } from '../redux/main/accountSlice';
// Rimuoviamo l'import di selectColorsSchema perché useremo useTheme
import DynamicIcon from '../components/DynamicIcon';
import DynamicTitle from '../components/DynamicTitle';
import { formatDurationMinutes, formatDistance } from '../utils/formatUtils.js';
import { useTheme, ThemedBox, ThemedText, ThemedIcon, ThemedButton } from '../components/ThemeProvider';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale } from 'react-datepicker';
import it from 'date-fns/locale/it';

// Registra il locale italiano per DatePicker
registerLocale('it', it);

// Helper per formattare una data in YYYY-MM-DD usando il fuso orario locale (evita il bug UTC di toISOString)
const toLocalDateString = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Componente modale di conferma
const ConfirmModal = ({ isOpen, onCancel, onConfirm, maintenanceData, isLoading, isEditMode }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 border-b pb-2">
          {isEditMode ? 'Conferma modifica manutenzione' : 'Conferma creazione manutenzione'}
        </h2>
        
        <div className="space-y-3 mb-6">
          <p>{isEditMode 
              ? `Sei sicuro di voler modificare questa manutenzione (${maintenanceData.code})?` 
              : 'Sei sicuro di voler creare questa manutenzione?'}</p>
          {!isEditMode && (
            <p className="text-sm text-gray-600">Dopo la creazione, potrai continuare ad utilizzare lo stesso wizard per creare ulteriori manutenzioni.</p>
          )}
          
          {/* Il riepilogo dati è stato rimosso come richiesto */}
        </div>
        
        <div className="flex justify-end gap-3">
          <ThemedButton 
            buttonType="outline"
            variant="outlined"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-md"
          >
            Annulla
          </ThemedButton>
          <ThemedButton 
            buttonType="primary"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 rounded-md flex items-center"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {isEditMode ? 'Salvataggio in corso...' : 'Creazione in corso...'}
              </>
            ) : (isEditMode ? 'Modifica Manutenzione' : 'Crea Manutenzione')}
          </ThemedButton>
        </div>
      </div>
    </div>
  );
};

// Array degli step del wizard con icone
const steps = [
  { title: 'Cliente e Impianto', icon: 'fa-user-tie' },
  { title: 'Azioni Programmate', icon: 'fa-calendar-check' },
  { title: 'Note e Dettagli', icon: 'fa-clipboard-list' }
];

// Componente per l'icona dello step utilizzando il tema dinamico
const StepIcon = ({ icon, isActive, isCompleted }) => {
  const theme = useTheme();
  
  // Definiamo colori di sfondo, testo e bordo basati sul tema
  let bgColor, textColor, borderColor;
  
  if (isCompleted) {
    bgColor = theme.buttons?.success?.background || '#4caf50';
    textColor = theme.buttons?.success?.text || '#ffffff';
    borderColor = theme.buttons?.success?.border || bgColor;
  } else if (isActive) {
    bgColor = theme.primary || '#2196f3';
    textColor = theme.textOnPrimary || '#ffffff';
    borderColor = theme.primary || '#2196f3';
  } else {
    bgColor = '#f5f5f5';
    textColor = '#9e9e9e';
    borderColor = '#e0e0e0';
  }
  
  return (
    <div className="w-14 h-14 rounded-full flex items-center justify-center"
      style={{
        backgroundColor: isCompleted || isActive ? `${bgColor}20` : bgColor, // Aggiungiamo trasparenza per active/completed
        color: textColor,
        borderWidth: '2px',
        borderStyle: 'solid',
        borderColor: borderColor
      }}>
      <ThemedIcon 
        iconType={isCompleted ? 'success' : isActive ? 'primary' : 'disabled'}
        className={`fa-solid ${icon} text-xl`} 
      />
    </div>
  );
};

// Stato iniziale per il reset del form
const initialFormState = {
  customer: null,
  system: null,
  systemTypes: [], // Array di tipi di impianto selezionati, non più singolo
  actions: [], // Array delle azioni programmate di manutenzione
  notes: '',
  attachments: [],
  // Campi contratto
  contractStartDate: '',
  contractEndDate: '',
  contractFile: null
};

// Stato iniziale per la validazione
const initialValidationState = {
  customer: false,
  system: false,
  systemTypes: false,
  actions: false
};

const CreateMaintenance = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: maintenanceId } = useParams(); // Get ID from URL params
  const isEditMode = !!maintenanceId; // Determine if we're in edit mode
  
  const accountData = useSelector(selectAccountData);
  // Utilizziamo il nostro hook useTheme per accedere alla palette colori dinamica
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(initialFormState);
  const [validation, setValidation] = useState(initialValidationState);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [systemsList, setSystemsList] = useState([]);
  const [systemTypesList, setSystemTypesList] = useState([]);
  const [lastSelectedCustomerId, setLastSelectedCustomerId] = useState(null);
  const [isLoading, setIsLoading] = useState(isEditMode); // Start loading if in edit mode
  const [maintenanceLoaded, setMaintenanceLoaded] = useState(false); // Track if maintenance was loaded successfully
  const [showErrorModal, setShowErrorModal] = useState(false); // Modale errore dati mancanti
  const [errorModalMessage, setErrorModalMessage] = useState(''); // Messaggio errore
  // Conferma per le azioni cancellate che risultano collegate a delle commesse:
  // il backend risponde 409 e il salvataggio va ripetuto con force:true
  const [linkedActionsWarning, setLinkedActionsWarning] = useState(null); // { message, linkedActions[] }
  const [pendingMaintenanceData, setPendingMaintenanceData] = useState(null); // payload da rinviare
  // Azioni gia' collegate a delle commesse: { [actionId]: [{id, code, statusLabel}] }.
  // Su queste lo stato lo comanda la commessa, quindi la tendina va bloccata.
  const [actionJobLinks, setActionJobLinks] = useState({});
  
  const token = localStorage.getItem('installamelo-auth-token');
  
  // Effetto per aggiornare il titolo della pagina
  useEffect(() => {
    document.title = isEditMode ? 'Modifica Manutenzione | Installamelo' : 'Crea Manutenzione | Installamelo';
  }, [isEditMode]);
  
  // Verifica se l'account ha impostazioni necessarie
  useEffect(() => {
    if (accountData && (!accountData.settings || !accountData.settings.maintenanceStatus)) {
      toast.error('Impostazioni account non complete. Impossibile gestire manutenzioni.');
    }
  }, [accountData]);
  
  // Funzione di reset e reinizializzazione del form
  const resetForm = async (newData) => {
    console.log('Resetting form with new data:', newData);
    // Reset completo dello stato
    setFormData(initialFormState);
    setValidation(initialValidationState);
    
    // Breve attesa per assicurarsi che il reset sia completo
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Imposta i nuovi dati
    setFormData(newData);
    
    // Marca come caricato con successo
    setMaintenanceLoaded(true);
  };
  
  // Carica i dati della manutenzione se in modalità modifica
  useEffect(() => {
    const loadMaintenanceData = async () => {
      if (isEditMode && maintenanceId && accountData && token && !maintenanceLoaded) {
        console.log('Modalità modifica attivata, maintenanceId:', maintenanceId);
        setIsLoading(true);
        
        try {
          // Effettua la chiamata API per ottenere i dettagli della manutenzione
          console.log('Chiamata API per ottenere i dettagli:', `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances?maintenanceId=${maintenanceId}`);
          
          const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`, {
            headers: { 'auth-token': token },
            params: { maintenanceId }
          });

          // Quali azioni hanno gia' una commessa collegata: su quelle lo stato lo comanda
          // la commessa. Se la chiamata fallisce si prosegue lo stesso, semplicemente le
          // tendine restano tutte modificabili.
          try {
            const linksResponse = await axios.get(
              `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances/${maintenanceId}/actionLinks`,
              { headers: { 'auth-token': token } }
            );
            const links = linksResponse.data?.ret || linksResponse.data || [];
            const linksMap = {};
            (Array.isArray(links) ? links : []).forEach(l => {
              if (l.jobs && l.jobs.length > 0) linksMap[l.actionId] = l.jobs;
            });
            setActionJobLinks(linksMap);
          } catch (linksError) {
            console.log('Collegamenti azioni-commesse non recuperati:', linksError.message);
          }
          
          console.log('Risposta API completa:', response);
          
          // La risposta API potrebbe essere strutturata in due modi diversi:
          // 1. response.data direttamente contiene i dati della manutenzione
          // 2. response.data.ret contiene i dati della manutenzione
          
          // Determina quale formato è stato ricevuto
          let maintenance;
          if (response.data && response.data._id) {
            // Case 1: I dati sono direttamente in response.data
            maintenance = response.data;
            console.log('Dati manutenzione trovati direttamente in response.data');
          } else if (response.data && response.data.ret && response.data.ret._id) {
            // Case 2: I dati sono in response.data.ret
            maintenance = response.data.ret;
            console.log('Dati manutenzione trovati in response.data.ret');
          } else {
            console.error('Dati manutenzione non trovati nella risposta', response.data);
            toast.error('Dati della manutenzione non trovati o incompleti.');
            setIsLoading(false);
            return;
          }
          console.log('Dati manutenzione caricati:', maintenance);
          
          if (!maintenance.customerId || !maintenance.systemId) {
            console.error('Dati manutenzione incompleti: manca customerId o systemId', maintenance);
            toast.error('Dati della manutenzione incompleti. Impossibile caricare i dettagli.');
            setIsLoading(false);
            return;
          }
          
          // Prepara i dati cliente
          const customerData = {
            id: maintenance.customerId,
            value: maintenance.customerId,
            label: maintenance.customerLabel,
            customerId: maintenance.customerId,
            customerLabel: maintenance.customerLabel
          };
          console.log('CustomerData preparato:', customerData);
          
          // Aggiornamento del cliente selezionato
          setLastSelectedCustomerId(maintenance.customerId);
          
          // Carica gli impianti per questo cliente
          console.log('Caricamento impianti per il cliente:', maintenance.customerId);
          const systemsResponse = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/systems/systemsList`, {
            headers: { 'auth-token': token },
            params: { 
              accountId: accountData._id,
              customerId: maintenance.customerId
            }
          });
          
          console.log('Risposta impianti:', systemsResponse.data);
          
          if (!systemsResponse.data || systemsResponse.data.length === 0) {
            console.warn('Nessun impianto trovato per il cliente:', maintenance.customerId);
            setErrorModalMessage(`Impossibile ${isEditMode ? 'modificare' : 'clonare'} questa manutenzione.\n\nL'impianto associato al cliente "${maintenance.customerLabel || 'N/D'}" non è più disponibile.\n\nPotrebbe essere stato eliminato dal sistema.`);
            setShowErrorModal(true);
            setIsLoading(false);
            return;
          }
          
          // Aggiorna la lista degli impianti
          setSystemsList(systemsResponse.data);
          
          // Trova l'impianto giusto
          const system = systemsResponse.data.find(s => s._id === maintenance.systemId);
          console.log('Sistema trovato:', system, 'cercando ID:', maintenance.systemId);
          
          let systemToUse = system;
          
          if (!system) {
            console.error('Sistema non trovato tra quelli caricati! ID cercato:', maintenance.systemId);
            
            // Se non lo trova esattamente, prendi il primo della lista come fallback
            const fallbackSystem = systemsResponse.data[0];
            console.log('Usando sistema fallback:', fallbackSystem);
            toast.warning('Impianto originale non trovato. Usando il primo impianto disponibile.');
            
            if (fallbackSystem) {
              systemToUse = fallbackSystem;
            } else {
              console.error('Nessun sistema disponibile come fallback!');
              toast.error('Impossibile trovare alcun impianto per questo cliente.');
              setIsLoading(false);
              return;
            }
          }
          
          try {
            // Assicurati che maintenance.systemTypes sia un array
            const systemTypesArray = Array.isArray(maintenance.systemTypes) ? 
              maintenance.systemTypes : [];
            
            // Prepara i dati dei tipi di impianto
            const systemTypesData = systemTypesArray.map(type => ({
              typeId: type.systemTypeId,
              typeLabel: type.systemTypeLabel,
              id: type.systemTypeId,
              label: type.systemTypeLabel
            }));
            console.log('System Types preparati:', systemTypesData);
            
            // Assicurati che maintenance.actions sia un array
            const actionsArray = Array.isArray(maintenance.actions) ? 
              maintenance.actions : [];
            
            // Prepara le azioni con i dati dello stato corretti.
            // L'_id va conservato: e' il riferimento usato dalle commesse
            // (Job.maintenanceActions.actionId). Perderlo qui fa credere al backend che
            // tutte le azioni siano state cancellate e blocca il salvataggio.
            const actionsData = actionsArray.map(action => ({
              _id: action._id,
              title: action.title,
              notes: action.notes || '',
              month: parseInt(action.month) || 1,
              year: parseInt(action.year) || new Date().getFullYear(),
              status: {
                id: action.statusId || '0',
                label: action.statusLabel || 'Da Pianificare'
              }
            }));
            console.log('Actions preparate:', actionsData);
            
            // Aggiorna formData con tutti i dati caricati
            const newFormData = {
              customer: customerData,
              system: systemToUse,
              systemTypes: systemTypesData,
              actions: actionsData,
              notes: maintenance.notes || '',
              _id: maintenance._id, // Salva l'ID per l'aggiornamento
              code: maintenance.code, // Salva il codice manutenzione
              // Campi contratto
              contractStartDate: toLocalDateString(maintenance.contractStartDate),
              contractEndDate: toLocalDateString(maintenance.contractEndDate),
              contractFile: maintenance.contractFile || null
            };
            
            console.log('Nuovo formData completo da impostare:', newFormData);
            
            // Usa la funzione di reset e inizializzazione
            await resetForm(newFormData);
            
            // Resetta il currentStep a 0 esplicitamente (primo step) quando siamo in modalità modifica
            setCurrentStep(0);
            
            // Tutti i dati necessari sono caricati, resetta lo stato di validazione
            setValidation({
              customer: false,
              system: false,
              systemTypes: false,
              actions: false
            });
            
            // Verifica se i dati sono stati aggiornati correttamente dopo un breve ritardo
            setTimeout(() => {
              console.log('FormData dopo aggiornamento:', formData);
              console.log('CurrentStep:', currentStep);
              console.log('Validation:', validation);
            }, 300);
            
            toast.success('Dati della manutenzione caricati con successo!');
          } catch (typeError) {
            console.error('Errore nella preparazione dei dati:', typeError);
            toast.error('Errore nella preparazione dei dati della manutenzione.');
          }
        } catch (error) {
          console.error('Errore nel caricamento della manutenzione:', error);
          toast.error('Errore nel caricamento dei dati della manutenzione. Riprova più tardi.');
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    loadMaintenanceData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, maintenanceId, accountData, token, maintenanceLoaded]);
  
  // Effetto aggiuntivo per verificare quando formData cambia
  useEffect(() => {
    if (isEditMode && formData._id) {
      console.log('FormData è stato aggiornato:', formData);
    }
  }, [formData, isEditMode]);
  
  // Carica l'elenco dei tipi di sistema quando cambia l'account
  useEffect(() => {
    if (accountData?.settings?.systemTypes) {
      setSystemTypesList(accountData.settings.systemTypes);
    }
  }, [accountData]);
  
  // Gestione selezione cliente
  const handleCustomerSelect = async (selectedOption) => {
    console.log('Cliente selezionato:', selectedOption);
    
    if (!selectedOption) {
      setFormData(prev => ({ ...prev, customer: null, system: null, systemTypes: [] }));
      setSystemsList([]);
      return;
    }
    
    // Estrae l'ID dal cliente selezionato (potrebbe essere in value o customerId)
    const customerId = selectedOption.value || selectedOption.customerId || selectedOption.id;
    
    // Se è lo stesso cliente, non fare nulla
    if (customerId === lastSelectedCustomerId) return;
    
    setLastSelectedCustomerId(customerId);
    
    // Crea un oggetto cliente normalizzato per l'uso interno
    const customerObject = {
      id: customerId,
      value: customerId,
      label: selectedOption.label || selectedOption.customerLabel || 'Cliente selezionato',
      ...selectedOption
    };
    
    setFormData(prev => ({ ...prev, customer: customerObject, system: null, systemTypes: [] }));
    
    // Resetta la validazione per questo campo
    setValidation(prev => ({
      ...prev,
      customer: false
    }));
    
    // Carica gli impianti per questo cliente
    try {
      const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL || ''}/systems/systemsList`, {
        headers: { 'auth-token': token },
        params: { 
          accountId: accountData._id,
          customerId: customerId
        }
      });
      
      console.log('Risposta sistemi:', response.data);
      
      // L'API /systems/systemsList restituisce direttamente l'array di sistemi
      if (response.data) {
        setSystemsList(response.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento degli impianti:', error);
      toast.error('Errore nel caricamento degli impianti. Riprova più tardi.');
    }
  };
  
  // Gestione selezione impianto
  const handleSystemSelect = (system) => {
    console.log('Sistema selezionato:', system);
    // Assicurati che l'ID dell'impianto sia sempre conservato come _id
    const normalizedSystem = {
      ...system,
      _id: system._id || system.id  // Usa _id come proprietà standard
    };
    
    setFormData(prev => ({ ...prev, system: normalizedSystem, systemTypes: [] }));
    
    // Resetta la validazione per questo campo
    setValidation(prev => ({
      ...prev,
      system: false
    }));
  };
  
  // Gestione selezione tipo impianto (toggle - aggiungi/rimuovi)
  const handleSystemTypeSelect = (systemType) => {
    console.log('Tipo impianto selezionato:', systemType);
    
    // Converti systemType in un formato standard
    const standardizedType = {
      ...systemType,
      id: systemType.typeId,  // Per compatibilità
      label: systemType.typeLabel  // Per compatibilità
    };
    
    setFormData(prev => {
      // Controlla se il tipo è già selezionato
      const existingTypeIndex = prev.systemTypes.findIndex(
        type => type.typeId === standardizedType.typeId
      );
      
      // Crea un nuovo array di tipi di impianto
      let updatedSystemTypes;
      
      if (existingTypeIndex !== -1) {
        // Se già selezionato, rimuovilo (toggle off)
        updatedSystemTypes = prev.systemTypes.filter((_, index) => index !== existingTypeIndex);
      } else {
        // Se non selezionato, aggiungilo (toggle on)
        updatedSystemTypes = [...prev.systemTypes, standardizedType];
      }
      
      return {
        ...prev,
        systemTypes: updatedSystemTypes
      };
    });
  };
  
  // Verifica se un tipo di impianto è selezionato
  const isSystemTypeSelected = (typeId) => {
    return formData.systemTypes.some(type => type.typeId === typeId);
  };
  
  // Aggiunta di una nuova azione pianificata
  const handleAddAction = (action) => {
    setFormData(prev => ({
      ...prev,
      actions: [...prev.actions, action]
    }));
    
    // Resetta la validazione per questo campo
    setValidation(prev => ({
      ...prev,
      actions: false
    }));
  };
  
  // Rimozione di un'azione pianificata
  const handleRemoveAction = (index) => {
    setFormData(prev => ({
      ...prev,
      actions: prev.actions.filter((_, i) => i !== index)
    }));
  };
  
  // Gestione delle note direttamente nell'elemento invece che nel form data
  const notesTextareaRef = useRef(null);
  
  // Gestione modifiche note - versione semplice senza re-render
  const handleNotesChange = () => {
    // Non facciamo nulla qui, manteniamo il valore nella textarea stessa
    // Questo impedirà che React reinizializzi il cursore ad ogni digitazione
  };
  
  // Aggiorna formData con le note quando necessario
  const syncNotes = () => {
    if (notesTextareaRef.current) {
      const textareaValue = notesTextareaRef.current.value;
      if (textareaValue !== formData.notes) {
        setFormData(prev => ({ ...prev, notes: textareaValue }));
      }
    }
  };
  
  // Validazione dello step corrente
  const validateCurrentStep = () => {
    // Sincronizziamo le note prima della validazione
    if (currentStep === 2) {
      syncNotes();
    }
    
    let isValid = true;
    const newValidation = { ...initialValidationState };
    
    switch (currentStep) {
      case 0: // Cliente e Impianto
        if (!formData.customer) {
          newValidation.customer = true;
          isValid = false;
        }
        
        if (!formData.system) {
          newValidation.system = true;
          isValid = false;
        }
        
        if (!formData.systemTypes || formData.systemTypes.length === 0) {
          newValidation.systemTypes = true;
          isValid = false;
        }
        break;
        
      case 1: // Azioni Programmate
        if (formData.actions.length === 0) {
          newValidation.actions = true;
          isValid = false;
        }
        break;
        
      case 2: // Note e Dettagli
        // Non ci sono campi obbligatori in questo step
        break;
    }
    
    setValidation(newValidation);
    return isValid;
  };
  
  // Gestione pulsante Avanti
  const handleNext = () => {
    // Non sincronizziamo più le note nell'ultimo step per mostrare direttamente la finestra di conferma
    
    if (validateCurrentStep()) {
      if (currentStep < steps.length - 1) {
        // Se non è l'ultimo step, sincronizziamo le note se necessario
        if (currentStep === 2) {
          syncNotes();
        }
        setCurrentStep(currentStep + 1);
      } else {
        // Se siamo nell'ultimo step, mostriamo direttamente la modale di conferma
        setShowConfirmModal(true);
      }
    }
  };
  
  // Gestione pulsante Indietro
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  // Annulla la creazione
  const handleCancel = () => {
    // Chiede conferma prima di annullare
    if (window.confirm('Sei sicuro di voler annullare? Tutte le modifiche andranno perse.')) {
      navigate('/maintenances');
    }
  };
  
  // Conferma la creazione o modifica della manutenzione
  const handleConfirmCreate = async () => {
    setIsSubmitting(true);
    
    try {
      // Prepara i dati per l'invio
      console.log('Dati sistema per invio:', formData.system);
      console.log('Tipi impianto selezionati:', formData.systemTypes);
      
      const maintenanceData = {
        accountId: accountData._id,
        customerId: formData.customer.customerId || formData.customer.id || formData.customer._id,
        customerLabel: formData.customer.customerLabel || formData.customer.label,
        
        // Usa _id come identificatore primario per il sistema
        systemId: formData.system._id,
        systemLabel: formData.system.title || formData.system.label,
        
        // Indirizzo del sistema - usa customerAddressId corretto
        systemAddressId: formData.system.customerAddressId || '', // usando customerAddressId come richiesto
        systemAddressLabel: formData.system.customerAddressLabel || formData.system.address || '',
        
        // Aggiungi i dati geografici se presenti nel sistema
        ...(formData.system.geoData && { geoData: formData.system.geoData }),
        
        // Formatta i tipi di sistema secondo lo schema richiesto
        systemTypes: formData.systemTypes.map(type => ({
          systemTypeId: type.typeId || type.id || type._id,
          systemTypeLabel: type.typeLabel || type.label
        })),

        // Mappa le azioni programmate.
        // L'_id delle azioni gia' esistenti va sempre rispedito: e' quello che il backend
        // usa per capire quali azioni sono state davvero cancellate e per non rompere il
        // collegamento con le commesse (Job.maintenanceActions.actionId).
        // Le azioni nuove non hanno _id e il backend lo genera.
        actions: formData.actions.map(action => {
          const baseAction = {
            ...(action._id ? { _id: action._id } : {}),
            title: action.title,
            notes: action.notes,
            month: action.month,
            year: action.year
          };

          // Se l'azione ha già uno stato definito, lo usa
          if (action.status && action.status.id) {
            return {
              ...baseAction,
              statusId: action.status.id.toString(),
              statusLabel: action.status.label
            };
          }

          // Altrimenti, assicurati di usare lo stato predefinito
          const defaultStatus = accountData?.settings?.maintenanceStatus?.find(status => status.default === true);
          const statusId = defaultStatus?._id || "0";
          const statusLabel = defaultStatus?.label || "";

          return {
            ...baseAction,
            statusId: statusId.toString(),
            statusLabel: statusLabel
          };
        }),
        notes: formData.notes,
        // Campi contratto - aggiungiamo T12:00:00 per evitare shift di timezone UTC
        contractStartDate: formData.contractStartDate ? new Date(formData.contractStartDate + 'T12:00:00') : null,
        contractEndDate: formData.contractEndDate ? new Date(formData.contractEndDate + 'T12:00:00') : null,
        contractFile: formData.contractFile || null
      };
      
      console.log('[CreateMaintenance] Dati contratto da inviare:', {
        contractStartDate: maintenanceData.contractStartDate,
        contractEndDate: maintenanceData.contractEndDate,
        contractFile: maintenanceData.contractFile
      });
      
      // Se in modalità modifica, aggiungi l'ID della manutenzione
      if (isEditMode) {
        maintenanceData._id = formData._id;
      }

      await inviaManutenzione(maintenanceData, false);
    } catch (error) {
      console.error(`Errore nella ${isEditMode ? 'modifica' : 'creazione'} della manutenzione:`, error);
      toast.error(`Errore nella ${isEditMode ? 'modifica' : 'creazione'} della manutenzione. Riprova più tardi.`);
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  // Invio effettivo al backend. Con force=true si conferma la cancellazione di azioni
  // ancora collegate a delle commesse, accettando di perdere quel collegamento.
  const inviaManutenzione = async (maintenanceData, force) => {
    try {
      const payload = { maintenance: maintenanceData };
      if (force) payload.force = true;

      let response;
      if (isEditMode) {
        response = await axios.patch(
          `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`,
          payload,
          { headers: { 'auth-token': token } }
        );
        if (response.data) {
          toast.success('Manutenzione aggiornata con successo!');
          navigate('/maintenances');
        }
      } else {
        response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL || ''}/maintenances`,
          payload,
          { headers: { 'auth-token': token } }
        );
        if (response.data) {
          toast.success('Manutenzione creata con successo!');
          navigate('/maintenances');
        }
      }

      setIsSubmitting(false);
      setShowConfirmModal(false);
      setLinkedActionsWarning(null);
      setPendingMaintenanceData(null);
    } catch (error) {
      // Il backend blocca il salvataggio se si stanno cancellando azioni collegate a
      // delle commesse: non è un errore, va chiesta conferma esplicita all'utente.
      if (error.response?.status === 409 && error.response.data?.requiresConfirmation) {
        setPendingMaintenanceData(maintenanceData);
        setLinkedActionsWarning({
          message: error.response.data.message || 'Alcune azioni che stai eliminando sono collegate a delle commesse.',
          linkedActions: error.response.data.linkedActions || []
        });
        setIsSubmitting(false);
        setShowConfirmModal(false);
        return;
      }

      console.error(`Errore nella ${isEditMode ? 'modifica' : 'creazione'} della manutenzione:`, error);
      toast.error(error.response?.data?.message || `Errore nella ${isEditMode ? 'modifica' : 'creazione'} della manutenzione. Riprova più tardi.`);
      setIsSubmitting(false);
      setShowConfirmModal(false);
    }
  };

  // L'utente conferma di voler cancellare comunque le azioni collegate
  const handleConfermaAzioniCollegate = async () => {
    if (!pendingMaintenanceData) return;
    setLinkedActionsWarning(null);
    setIsSubmitting(true);
    await inviaManutenzione(pendingMaintenanceData, true);
  };
  
  // Componente per il controllo delle azioni
  const ActionControls = () => {
    const isLastStep = currentStep === steps.length - 1;
    
    return (
      <div className="flex justify-between mt-8">
        <div>
          <ThemedButton
            buttonType="outline"
            variant="outlined"
            onClick={handleCancel}
            className="px-4 py-2 rounded-md"
            title="Torna alla lista delle manutenzioni"
          >
            Annulla
          </ThemedButton>
        </div>
        
        <div className="flex gap-3">
          {currentStep > 0 && (
            <ThemedButton
              buttonType="secondary"
              variant="outlined"
              onClick={handleBack}
              className="px-4 py-2 rounded-md"
            >
              Indietro
            </ThemedButton>
          )}
          
          <ThemedButton
            buttonType="primary"
            onClick={handleNext}
            className="px-4 py-2 rounded-md"
          >
            {isLastStep ? (isEditMode ? 'Salva Modifiche' : 'Completa') : 'Avanti'}
          </ThemedButton>
        </div>
      </div>
    );
  };
  
  // Componente per il primo step: Cliente e Impianto
  const ClientSystemStep = () => {
    // Mantieni sempre l'ultimo cliente selezionato
    useEffect(() => {
      console.log('Cliente selezionato:', formData.customer);
    }, [formData.customer]);
    
    // Stato locale per gestire il caricamento dei sistemi
    const [customerSystemsLoading, setCustomerSystemsLoading] = useState(false);
    
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Cliente</h3>
          <CustomerSelect
            onChange={handleCustomerSelect}
            placeholder="Seleziona un cliente..."
            value={formData.customer}
          />
          {validation.customer && (
            <p className="text-red-500 text-sm">Seleziona un cliente</p>
          )}
        </div>
        
        {formData.customer && (
          <div className="border-t pt-4 mt-4">
            <h3 className="text-lg font-semibold mb-4">Impianti disponibili</h3>
            
            {customerSystemsLoading ? (
              <div className="py-4 text-center text-gray-500">
                <svg className="animate-spin h-6 w-6 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Caricamento impianti...
              </div>
            ) : systemsList.length === 0 ? (
              <div className="p-4 text-center bg-gray-50 border rounded-lg">
                <p className="text-gray-500">Nessun impianto disponibile per questo cliente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {systemsList.map(system => (
                  <div 
                    key={system._id}
                    className="border rounded-lg p-4 transition-all hover:bg-gray-50"
                  >
                    <div 
                      className={`p-2 mb-2 rounded cursor-pointer ${formData.system?._id === system._id ? 'bg-blue-100 text-blue-800' : 'bg-gray-50'}`}
                      onClick={() => handleSystemSelect(system)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="font-medium">{system.title}</div>
                          <div className="text-sm text-gray-600">{system.customerAddressLabel || system.address}</div>
                          
                          {/* Informazioni di distanza e tempo */}
                          {system.geoData && (system.geoData.distanceKm || system.geoData.distanceTime) && (
                            <div className="mt-2 flex items-center space-x-3 text-xs">
                              {system.geoData.distanceKm > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                  <i className="fa-solid fa-route mr-1 text-blue-500"></i>
                                  {formatDistance(system.geoData.distanceKm)}
                                </span>
                              )}
                              {system.geoData.distanceTime > 0 && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                                  <i className="fa-solid fa-clock mr-1 text-blue-500"></i>
                                  {formatDurationMinutes(system.geoData.distanceTime)}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          {formData.system?._id === system._id ? (
                            <span className="text-xs bg-blue-500 text-white px-2 py-1 rounded-full">Selezionato</span>
                          ) : (
                            <button 
                              className="text-xs px-2 py-1 border border-blue-500 text-blue-500 rounded hover:bg-blue-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSystemSelect(system);
                              }}
                            >
                              Seleziona
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Mostra i tipi di impianto solo per il sistema selezionato */}
                    {system.typesList && system.typesList.length > 0 && formData.system?._id === system._id && (
                      <div className="mt-3 border-t pt-2">
                        <div className="text-sm font-medium mb-1">Tipi di impianto:</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {system.typesList.map(type => {
                            // Controlla se questo tipo è selezionato
                            const isSelected = isSystemTypeSelected(type.typeId);
                            
                            // Determina se è selezionabile (solo per l'impianto selezionato)
                            const isSelectable = formData.system?._id === system._id;
                            
                            // Calcola stato di garanzia
                            const hasWarranty = type.expirationWarrantyDate;
                            const warrantyDate = hasWarranty ? new Date(type.expirationWarrantyDate) : null;
                            const isWarrantyExpired = hasWarranty && warrantyDate < new Date();
                            
                            // Data di collaudo
                            const testedDate = type.testedDate ? new Date(type.testedDate) : null;
                            
                            return (
                              <div 
                                key={type.typeId}
                                className={`
                                  px-3 py-2 text-base rounded-md ${isSelectable ? 'cursor-pointer' : 'cursor-not-allowed'}
                                  ${isSelected ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                                  ${isSelectable ? 'hover:bg-opacity-80' : 'opacity-70'}
                                `}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (isSelectable) {
                                    handleSystemTypeSelect({
                                      ...type,
                                      systemId: system._id
                                    });
                                  }
                                }}
                              >
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">{type.typeLabel}</span>
                                  <span className="text-sm">
                                    {isSelected ? 
                                      <ThemedIcon iconType="success" className="fa-solid fa-check-circle" /> : 
                                      <ThemedIcon iconType="secondary" className="fa-regular fa-circle" />}
                                  </span>
                                </div>
                                                                {/* Info aggiuntive */}
                                <div className="mt-1 flex flex-wrap gap-2 text-sm">
                                  {testedDate && (
                                    <span className="inline-flex items-center px-3 py-1 rounded bg-gray-100 text-gray-600">
                                      <ThemedIcon iconType="primary" className="fa-solid fa-calendar-check mr-3" />
                                      <span><strong>Collaudato</strong>: {testedDate.toLocaleDateString()}</span>
                                    </span>
                                  )}
                                  
                                  {hasWarranty && (
                                    <span className={`inline-flex items-center px-3 py-1 rounded ${isWarrantyExpired ? 'bg-red-100 text-red-800' : 'bg-blue-100 text-blue-800'}`}>
                                      <ThemedIcon iconType={isWarrantyExpired ? "danger" : "success"} className={isWarrantyExpired ? "fa-solid fa-triangle-exclamation mr-3" : "fa-solid fa-shield-check mr-3"} />
                                      <span><strong>{isWarrantyExpired ? 'Garanzia scaduta' : 'In garanzia'}</strong>: {warrantyDate.toLocaleDateString()}</span>
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            {validation.system && (
              <p className="text-red-500 text-sm mt-2">Seleziona un impianto</p>
            )}
          </div>
        )}
      </div>
    );
  };
  
  // Componente per il secondo step: Azioni Programmate
  const ActionsStep = () => {
    // Cerca lo stato di default nelle impostazioni dell'account
    const getDefaultStatus = () => {
      const defaultStatus = accountData?.settings?.maintenanceStatus?.find(status => status.default === true);
      if (defaultStatus) {
        return { 
          id: defaultStatus.id || defaultStatus._id, 
          label: defaultStatus.label || defaultStatus.name 
        };
      }
      // Fallback se non trova uno stato di default
      return { id: "0", label: "da pianificare" };
    };

    // Ottiene l'anno corrente per il selettore degli anni
    const currentYear = new Date().getFullYear();
    
    // Genera le opzioni per gli anni (anno corrente, anno+1, anno+2)
    const yearOptions = [
      { value: currentYear.toString(), label: currentYear.toString() },
      { value: (currentYear + 1).toString(), label: (currentYear + 1).toString() },
      { value: (currentYear + 2).toString(), label: (currentYear + 2).toString() }
    ];
    
    // Stato per tenere traccia dell'indice dell'azione in fase di modifica, -1 indica che non si sta modificando nulla
    const [editingIndex, setEditingIndex] = useState(-1);
    
    const [newAction, setNewAction] = useState({
      title: '',
      notes: '',
      month: new Date().getMonth() + 1,
      year: currentYear.toString(),
      // Usiamo sempre lo stato di default, ora nascosto all'utente
      status: getDefaultStatus()
    });
    
    const months = [
      { value: 1, label: 'Gennaio' },
      { value: 2, label: 'Febbraio' },
      { value: 3, label: 'Marzo' },
      { value: 4, label: 'Aprile' },
      { value: 5, label: 'Maggio' },
      { value: 6, label: 'Giugno' },
      { value: 7, label: 'Luglio' },
      { value: 8, label: 'Agosto' },
      { value: 9, label: 'Settembre' },
      { value: 10, label: 'Ottobre' },
      { value: 11, label: 'Novembre' },
      { value: 12, label: 'Dicembre' }
    ];
    
    // Recupera le opzioni di stato dalle impostazioni account, con fallback ai valori predefiniti
    const statusOptions = accountData?.settings?.maintenanceStatus || [];
    
    // Log per debug
    useEffect(() => {
      console.log('Opzioni di stato disponibili:', statusOptions);
      console.log('Stato predefinito:', statusOptions.find(status => status.default == true));
    }, [statusOptions]);
    
    // Commesse collegate all'azione che si sta modificando (vuoto per una azione nuova)
    const editingActionJobs = editingIndex >= 0
      ? (actionJobLinks[String(formData.actions[editingIndex]?._id || '')] || [])
      : [];

    const handleActionChange = (e) => {
      const { name, value } = e.target;
      setNewAction(prev => ({
        ...prev,
        // Converti month in numero per mantenere coerenza con l'array months
        [name]: name === 'month' ? parseInt(value, 10) : value
      }));
    };
    
    const handleStatusChange = (e) => {
      const statusId = e.target.value;
      // Gli stati in settings hanno `id` oppure solo `_id`: senza questo controllo
      // s.id.toString() va in errore sugli stati che hanno solo `_id`.
      const status = statusOptions.find(s =>
        String(s.id ?? '') === String(statusId) || String(s._id ?? '') === String(statusId)
      );
      
      if (status) {
        setNewAction(prev => ({
          ...prev,
          status: { 
            id: status.id || status._id, 
            label: status.label || status.title || status.name 
          }
        }));
      }
    };
    
    const handleAddActionClick = () => {
      if (newAction.title.trim() === '') {
        toast.error('Inserisci un titolo per l\'azione');
        return;
      }
      
      if (editingIndex >= 0) {
        // Siamo in modalità modifica
        const updatedActions = [...formData.actions];
        updatedActions[editingIndex] = newAction;
        
        // Aggiorniamo direttamente lo stato
        setFormData(prev => ({
          ...prev,
          actions: updatedActions
        }));
        
        setEditingIndex(-1); // Esci dalla modalità modifica
        toast.success('Azione aggiornata con successo');
      } else {
        // Stiamo aggiungendo una nuova azione
        handleAddAction(newAction);
      }
      
      // Reset form
      setNewAction({
        title: '',
        notes: '',
        month: new Date().getMonth() + 1,
        year: currentYear.toString(),
        status: getDefaultStatus() // Manteniamo lo stato di default
      });
    };
    
    // Funzione per iniziare la modifica di un'azione
    const handleEditAction = (index) => {
      const actionToEdit = formData.actions[index];
      setNewAction({
        ...actionToEdit
      });
      setEditingIndex(index);
      // Scroll fino al form per aiutare l'utente a vedere cosa sta modificando
      window.scrollTo({
        top: document.querySelector('.bg-gray-50.p-4.rounded-lg').offsetTop - 100,
        behavior: 'smooth'
      });
    };
    
    return (
      <div className="space-y-6">
        <h3 className="text-lg font-semibold">Aggiungi azioni programmate</h3>
        
        <div className="bg-gray-50 p-4 rounded-lg space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titolo dell'azione
            </label>
            <input
              type="text"
              name="title"
              value={newAction.title}
              onChange={handleActionChange}
              placeholder="Es: Manutenzione ordinaria"
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mese
              </label>
              <select
                name="month"
                value={newAction.month}
                onChange={handleActionChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {months.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Anno
              </label>
              <select
                name="year"
                value={newAction.year}
                onChange={handleActionChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                {yearOptions.map(year => (
                  <option key={year.value} value={year.value}>
                    {year.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Stato dell'azione: si puo' cambiare a mano solo se l'azione non ha una
              commessa collegata, altrimenti lo stato lo comanda la commessa e verrebbe
              sovrascritto al primo cambio di stato di quest'ultima. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Stato
            </label>
            <select
              name="status"
              value={newAction.status?.id || ''}
              onChange={handleStatusChange}
              disabled={editingActionJobs.length > 0}
              className={`w-full border border-gray-300 rounded-md px-3 py-2 ${editingActionJobs.length > 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
            >
              {statusOptions.map(status => {
                const id = status.id || status._id;
                return (
                  <option key={id} value={id}>
                    {status.label || status.title || status.name}
                  </option>
                );
              })}
            </select>
            {editingActionJobs.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Stato gestito dalla commessa {editingActionJobs.map(j => j.code).filter(Boolean).join(', ')}:
                cambia quando cambia la commessa.
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note
            </label>
            <textarea
              name="notes"
              value={newAction.notes}
              onChange={handleActionChange}
              placeholder="Note aggiuntive..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-[80px]"
            />
          </div>
          
          <div className="pt-2 flex justify-between items-center">
            {editingIndex >= 0 ? (
              <button
                onClick={() => {
                  setEditingIndex(-1);
                  // Reset form
                  setNewAction({
                    title: '',
                    notes: '',
                    month: new Date().getMonth() + 1,
                    year: currentYear.toString(),
                    status: getDefaultStatus()
                  });
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Annulla modifica
              </button>
            ) : (
              <div>{/* Placeholder vuoto per mantenere il layout */}</div>
            )}
            
            <button
              onClick={handleAddActionClick}
              className={`px-4 py-2 ${editingIndex >= 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white rounded-md`}
            >
              <ThemedIcon iconType="primary" className={editingIndex >= 0 ? "fa-solid fa-save mr-2" : "fa-solid fa-plus mr-2"} /> 
              {editingIndex >= 0 ? 'Aggiorna azione' : 'Aggiungi azione'}
            </button>
          </div>
        </div>
        
        {validation.actions && (
          <p className="text-red-500">Aggiungi almeno un'azione programmata</p>
        )}
        
        {/* Lista delle azioni aggiunte */}
        {formData.actions.length > 0 && (
          <div className="space-y-3">
            <h4 className="font-medium">Azioni programmate ({formData.actions.length})</h4>
            <div className="space-y-2">
              {formData.actions.map((action, index) => (
                <div 
                  key={index} 
                  className="flex justify-between items-start p-3 border rounded-lg bg-white"
                >
                  <div className="space-y-1">
                    <div className="font-medium">{action.title}</div>
                    <div className="text-sm text-gray-600">
                      {months.find(m => m.value === action.month)?.label} {action.year} - 
                      <span className={`ml-1 ${
                        action.status.id === "0" ? "text-yellow-600" : 
                        action.status.id === "1" ? "text-blue-600" : 
                        "text-green-600"
                      }`}>
                        {action.status.label}
                      </span>
                    </div>
                    {action.notes && (
                      <div className="text-sm">{action.notes}</div>
                    )}
                    {(actionJobLinks[String(action._id || '')] || []).length > 0 && (
                      <div className="text-xs text-gray-500">
                        <ThemedIcon iconType="primary" className="fa-solid fa-link mr-1" />
                        Stato gestito dalla commessa {(actionJobLinks[String(action._id || '')] || []).map(j => j.code).filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditAction(index)}
                      className="text-blue-500 hover:text-blue-700"
                      title="Modifica"
                    >
                      <ThemedIcon iconType="primary" className="fa-solid fa-edit" />
                    </button>
                    <button
                      onClick={() => handleRemoveAction(index)}
                      className="text-red-500 hover:text-red-700"
                      title="Rimuovi"
                    >
                      <ThemedIcon iconType="danger" className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Componente per il terzo step: Note e Dettagli
  const NotesDetailsStep = () => {
    const [uploadingContract, setUploadingContract] = useState(false);
    const contractFileInputRef = useRef(null);
    
    // Inizializza la textarea con il valore corrente di formData.notes quando si monta
    useEffect(() => {
      if (notesTextareaRef.current && currentStep === 2) {
        notesTextareaRef.current.value = formData.notes || '';
      }
    }, [currentStep]);

    // Gestione upload file contratto
    const handleContractFileChange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      // Verifica tipo file (PDF, DOC, DOCX, immagini)
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo di file non supportato. Usa PDF, DOC, DOCX o immagini.');
        return;
      }
      
      // Verifica dimensione (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Il file è troppo grande. Dimensione massima: 10MB');
        return;
      }
      
      setUploadingContract(true);
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        formDataUpload.append('uploadName', file.name);
        formDataUpload.append('uploadTitle', `Contratto manutenzione - ${formData.customer?.label || 'Cliente'}`);
        formDataUpload.append('path', `maintenances/${accountData._id}/contracts`);
        
        const response = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/files/upload`,
          formDataUpload,
          {
            headers: {
              'auth-token': token,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        
        const fileUrl = response.data.url || response.data.fileUrl || response.data;
        
        // Salva l'oggetto con la struttura corretta per lo schema
        setFormData(prev => ({
          ...prev,
          contractFile: {
            urlFile: fileUrl,
            fileName: file.name,
            title: `Contratto manutenzione - ${formData.customer?.label || 'Cliente'}`,
            kind: file.type
          }
        }));
        
        toast.success('File contratto caricato con successo');
      } catch (error) {
        console.error('Errore upload contratto:', error);
        toast.error('Errore durante il caricamento del file');
      } finally {
        setUploadingContract(false);
      }
    };
    
    // Rimuovi file contratto
    const handleRemoveContractFile = () => {
      setFormData(prev => ({
        ...prev,
        contractFile: null
      }));
      if (contractFileInputRef.current) {
        contractFileInputRef.current.value = '';
      }
    };

    return (
      <div className="space-y-6">
        {/* Sezione Contratto */}
        <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <DynamicIcon iconClass="fa-solid fa-file-contract mr-2" />
            Dettagli Contratto
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* Data Inizio Contratto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Inizio Contratto
              </label>
              <DatePicker
                selected={formData.contractStartDate ? new Date(formData.contractStartDate) : null}
                onChange={(date) => {
                  // Calcola automaticamente la data fine a +1 anno
                  let endDate = null;
                  if (date) {
                    const endDateObj = new Date(date);
                    endDateObj.setFullYear(endDateObj.getFullYear() + 1);
                    endDate = toLocalDateString(endDateObj);
                  }
                  setFormData(prev => ({
                    ...prev,
                    contractStartDate: toLocalDateString(date),
                    contractEndDate: endDate || prev.contractEndDate
                  }));
                }}
                dateFormat="dd/MM/yyyy"
                locale="it"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholderText="Seleziona data"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
            
            {/* Data Fine Contratto */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Data Fine Contratto
              </label>
              <DatePicker
                selected={formData.contractEndDate ? new Date(formData.contractEndDate) : null}
                onChange={(date) => setFormData(prev => ({
                  ...prev,
                  contractEndDate: toLocalDateString(date)
                }))}
                dateFormat="dd/MM/yyyy"
                locale="it"
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholderText="Seleziona data"
                isClearable
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </div>
          </div>
          
          {/* Upload File Contratto */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              File Contratto
            </label>
            
            {formData.contractFile?.urlFile ? (
              <div className="flex items-center justify-between p-3 border border-green-300 bg-green-50 rounded-md">
                <div className="flex items-center">
                  <DynamicIcon iconClass="fa-solid fa-file-pdf text-green-600 mr-2" />
                  <span className="text-sm text-green-700">{formData.contractFile.fileName || 'File caricato'}</span>
                  <a 
                    href={formData.contractFile.urlFile} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="ml-2 text-blue-600 hover:underline text-sm"
                  >
                    Visualizza
                  </a>
                </div>
                <button
                  type="button"
                  onClick={handleRemoveContractFile}
                  className="text-red-500 hover:text-red-700"
                >
                  <DynamicIcon iconClass="fa-solid fa-times" />
                </button>
              </div>
            ) : (
              <div className="flex items-center">
                <input
                  ref={contractFileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  onChange={handleContractFileChange}
                  className="hidden"
                  id="contractFileInput"
                />
                <label
                  htmlFor="contractFileInput"
                  className={`flex items-center px-4 py-2 border border-gray-300 rounded-md cursor-pointer hover:bg-gray-100 ${uploadingContract ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploadingContract ? (
                    <>
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Caricamento...
                    </>
                  ) : (
                    <>
                      <DynamicIcon iconClass="fa-solid fa-upload mr-2" />
                      Carica File
                    </>
                  )}
                </label>
                <span className="ml-3 text-sm text-gray-500">PDF, DOC, DOCX o immagini (max 10MB)</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Sezione Note */}
        <div>
          <h3 className="text-lg font-semibold mb-2">Note</h3>
          <textarea
            ref={notesTextareaRef}
            defaultValue={formData.notes || ''}
            onChange={handleNotesChange}
            placeholder="Inserisci note o dettagli aggiuntivi sulla manutenzione..."
            className="w-full border border-gray-300 rounded-md px-3 py-2 min-h-[150px]"
            autoFocus={currentStep === 2}
            onBlur={syncNotes} // Sincronizza quando l'utente ha finito di digitare
          />
        </div>
      </div>
    );
  };
  
  // Funzione per renderizzare la preview della manutenzione
  const MaintenancePreview = () => {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 h-full">
        <h3 className="text-xl font-semibold mb-4 border-b pb-3" 
            style={{ color: theme.primary || theme.buttons?.primary?.background || '#1976d2' }}>
          <DynamicIcon iconClass="fa-solid fa-eye" className="mr-3" /> 
          Anteprima Manutenzione
        </h3>
        
        {!formData.customer ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <DynamicIcon iconClass="fa-solid fa-file-circle-question text-5xl mb-4" />
            <p>Seleziona un cliente e un impianto per visualizzare l'anteprima</p>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-3 rounded-lg border" 
                style={{ 
                  backgroundColor: theme.backgrounds?.light || '#e3f2fd', 
                  borderColor: theme.borders?.light || '#bbdefb' 
                }}>
              <h4 className="font-medium text-base mb-3" 
                  style={{ color: theme.primary || theme.buttons?.primary?.background || '#1976d2' }}>
                Informazioni Generali
              </h4>
              <div className="space-y-3 text-base">
                <div className="flex items-start">
                  <span className="font-medium w-32 text-gray-700">Cliente:</span>
                  <span className="flex-1">{formData.customer?.label || "Non specificato"}</span>
                </div>
                <div className="flex items-start">
                  <span className="font-medium w-32 text-gray-700">Impianto:</span>
                  <span className="flex-1">{formData.system?.title || "Non specificato"}</span>
                </div>
                {formData.systemTypes && formData.systemTypes.length > 0 && (
                  <div className="flex items-start">
                    <span className="font-medium w-32 text-gray-700">Tipi Impianto:</span>
                    <div className="flex-1">
                      {formData.systemTypes.map((type, index) => (
                        <span key={type.typeId} className="inline-block text-sm px-3 py-1 rounded mr-2 mb-1" 
                              style={{ 
                                backgroundColor: theme.backgrounds?.success || '#e8f5e9', 
                                color: theme.buttons?.success?.text || theme.text?.success || '#2e7d32' 
                              }}>
                          {type.typeLabel || type.label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {formData.actions && formData.actions.length > 0 && (
              <div className="p-3 rounded-lg border" 
                  style={{ 
                    backgroundColor: theme.backgrounds?.success || '#e8f5e9', 
                    borderColor: theme.borders?.success || '#c8e6c9' 
                  }}>
                <h4 className="font-medium text-base mb-3" 
                    style={{ color: theme.buttons?.success?.text || theme.text?.success || '#2e7d32' }}>
                  Azioni Programmate ({formData.actions.length})
                </h4>
                <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
                  {formData.actions.map((action, index) => (
                    <div key={index} className="bg-white p-3 rounded border border-gray-200 text-sm">
                      <div className="font-medium text-base">{action.title}</div>
                      <div className="text-sm text-gray-700 mt-2">
                        <span className="inline-block">
                          <ThemedIcon iconType="primary" className="fa-solid fa-calendar-days mr-3" />
                          <span>{new Date(0, action.month - 1).toLocaleString('it-IT', {month: 'long'})} {action.year}</span>
                        </span>
                        <span className="inline-block ml-4">
                          <ThemedIcon iconType="secondary" className="fa-solid fa-circle-info mr-3" />
                          <span>{action.status.label}</span>
                        </span>
                      </div>
                      {action.notes && (
                        <div className="mt-1 text-xs italic">{action.notes}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {formData.notes && (
              <div className="p-3 rounded-lg border" 
                  style={{ 
                    backgroundColor: theme.backgrounds?.warning || '#fffde7', 
                    borderColor: theme.borders?.warning || '#fff9c4' 
                  }}>
                <h4 className="font-medium text-base mb-3" 
                    style={{ color: theme.buttons?.warning?.text || theme.text?.warning || '#f57f17' }}>
                  Note
                </h4>
                <div className="text-base italic">{formData.notes}</div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="page">
      {isLoading && (
        <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex items-center justify-center">
          <div className="bg-white p-5 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Caricamento dati...</span>
          </div>
        </div>
      )}
      <DynamicTitle title={isEditMode ? `Modifica Manutenzione ${formData.code || ''}` : 'Crea Manutenzione'} />
      
      <h1>
        <span className="font-light">Manutenzioni / </span>
        {isEditMode ? `Modifica Manutenzione ${formData.code || ''}` : 'Crea Manutenzione'}
      </h1>
      
      {/* Header con stepper - largo quanto tutta la pagina */}
      <div className="bg-white rounded-lg shadow-sm p-6 mb-4">
        {/* Stepper */}
        <div>
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <React.Fragment key={index}>
                {/* Step con icona e titolo */}
                <div className="flex flex-col items-center">
                  <StepIcon 
                    icon={step.icon} 
                    isActive={currentStep === index}
                    isCompleted={currentStep > index} 
                  />
                  <div className="mt-2 text-center text-sm font-medium">
                    {step.title}
                  </div>
                </div>
                
                {/* Separatore tra step (tranne dopo l'ultimo) */}
                {index < steps.length - 1 && (
                  <div className="w-full h-1 mx-2"
                    style={{
                      backgroundColor: currentStep > index ? 
                        theme.buttons?.success?.background || theme.success || '#4caf50' : 
                        theme.disabled || '#e0e0e0'
                    }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      
      {/* Contenitore diviso: Wizard a sinistra e Preview a destra */}
      <div className="flex flex-row flex-nowrap w-full mb-8" style={{ gap: '20px' }}>
        {/* Contenuto wizard - colonna sinistra */}
        <div style={{ flex: '7 1 70%', backgroundColor: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', border: `1px solid ${theme.borders?.default || theme.border || '#ced4da'}` }}>
          {/* Contenuto dello step corrente */}
          <div className="mb-6">
            {currentStep === 0 && <ClientSystemStep />}
            {currentStep === 1 && <ActionsStep />}
            {currentStep === 2 && <NotesDetailsStep />}
          </div>
          
          {/* Pulsanti di controllo */}
          <ActionControls />
        </div>
        
        {/* Preview manutenzione - colonna destra */}
        <div style={{ flex: '3 1 30%', backgroundColor: theme.backgrounds?.light || theme.lightBg || '#f8f9fa', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
          <MaintenancePreview />
        </div>
      </div>
      
      {/* Modale di conferma */}
      <ConfirmModal 
        isOpen={showConfirmModal}
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmCreate}
        maintenanceData={formData}
        isLoading={isSubmitting}
        isEditMode={isEditMode}
      />
      
      {/* Modale di conferma per azioni cancellate ancora collegate a delle commesse */}
      {linkedActionsWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center mr-4">
                <DynamicIcon iconClass="fa-solid fa-link-slash text-yellow-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Azioni collegate a delle commesse</h2>
            </div>

            <p className="text-gray-600 mb-4">{linkedActionsWarning.message}</p>

            {linkedActionsWarning.linkedActions.length > 0 && (
              <div className="border border-gray-200 rounded-lg mb-4 max-h-60 overflow-y-auto">
                {linkedActionsWarning.linkedActions.map((azione, idx) => (
                  <div key={azione.actionId || idx} className="p-3 border-b border-gray-100 last:border-b-0">
                    <p className="font-medium text-gray-800 text-sm">
                      {azione.actionTitle || 'Azione senza titolo'}
                      {azione.actionPeriod && (
                        <span className="text-gray-500 font-normal"> — {azione.actionPeriod}</span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Commesse collegate: {(azione.jobs || []).map(j => j.code).filter(Boolean).join(', ') || 'N/D'}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 text-sm">
                <strong>Attenzione:</strong> confermando, le commesse elencate resteranno
                senza il riferimento all&apos;azione di manutenzione. L&apos;operazione non
                è reversibile. Se non volevi eliminarle, annulla e ripristina le azioni.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <ThemedButton
                buttonType="secondary"
                onClick={() => {
                  setLinkedActionsWarning(null);
                  setPendingMaintenanceData(null);
                }}
                className="px-6 py-2 rounded-md"
                disabled={isSubmitting}
              >
                Annulla
              </ThemedButton>
              <ThemedButton
                buttonType="primary"
                onClick={handleConfermaAzioniCollegate}
                className="px-6 py-2 rounded-md"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Salvataggio...' : 'Elimina comunque e salva'}
              </ThemedButton>
            </div>
          </div>
        </div>
      )}

      {/* Modale di errore per dati mancanti */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4">
                <DynamicIcon iconClass="fa-solid fa-exclamation-triangle text-red-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Errore</h2>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-600 whitespace-pre-line">{errorModalMessage}</p>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-yellow-800 text-sm">
                <strong>Nota:</strong> I dati originali della manutenzione (cliente, impianto o indirizzo) potrebbero essere stati eliminati. 
                La manutenzione resta visibile per storico ma non può essere modificata.
              </p>
            </div>
            
            <div className="flex justify-end">
              <ThemedButton 
                buttonType="primary"
                onClick={() => {
                  setShowErrorModal(false);
                  navigate('/maintenances');
                }}
                className="px-6 py-2 rounded-md"
              >
                Torna alla lista
              </ThemedButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateMaintenance;